import React from 'react';
import { Card, SaveButton, useSection } from '../AdminUI';
import { Switch } from '../../ui/switch';
import { ArrowUp, ArrowDown, ChevronsUp, ChevronsDown, RotateCcw, Eye, EyeOff, LayoutList, Layers } from 'lucide-react';
import { toast } from 'sonner';

const ALL_SECTIONS = [
  { key: 'hero', label: 'Hero Banner & Slider', desc: 'Top full-width slide showcase with CTA buttons' },
  { key: 'marquee', label: 'Logo Marquee', desc: 'Auto-scrolling brand logos strip' },
  { key: 'gamecards', label: 'Game / Service Cards', desc: 'Category tiles under the hero' },
  { key: 'promotions', label: 'Promotions Banner', desc: 'Big promo call-to-action banner' },
  { key: 'features', label: 'Feature Cards', desc: 'Small feature highlights (2x2 grid)' },
  { key: 'products', label: 'Products (B2C / B2B)', desc: 'Product tabs & interactive cards' },
  { key: 'exchanges', label: 'Exchange Listings', desc: 'Exchange rows & demo login access section' },
  { key: 'dealingTerms', label: 'Dealing Terms', desc: 'Min refill / withdrawal / timings rows' },
  { key: 'paymentsWeAccept', label: 'Payments We Accept', desc: 'Payment method logos strip' },
  { key: 'topRated', label: 'Top Rated', desc: 'Auto-curated top-rated items' },
  { key: 'originals', label: 'Platforms Showcase', desc: 'Original platforms grid showcase' },
  { key: 'inplay', label: 'GET ID (Platforms Table)', desc: 'GET ID platforms table section' },
];

const GLOBAL_SECTIONS = [
  { key: 'floatingSocial', label: 'Floating Social Buttons', desc: 'WhatsApp / Telegram / Instagram / YouTube fixed CTAs' },
  { key: 'rightSidebar', label: 'Right Sidebar', desc: 'Desktop-only right panel with interests / recent' }
];

const DEFAULT_ORDER = ALL_SECTIONS.map(s => s.key);

export default function SectionVisibilitySection() {
  const [visLocal, setVisLocal, saveVis] = useSection('sectionVisibility');
  const [orderLocal, setOrderLocal, saveOrder] = useSection('sectionOrder');
  const [heroCtrls, setHeroCtrls, saveHeroCtrls] = useSection('heroSliderControls');

  const curVis = visLocal || {};
  const setVis = (k, v) => setVisLocal({ ...curVis, [k]: v });

  const rawOrder = Array.isArray(orderLocal) && orderLocal.length > 0 ? orderLocal : DEFAULT_ORDER;
  // Ensure all sections are represented
  const currentOrder = [
    ...rawOrder.filter(k => ALL_SECTIONS.some(s => s.key === k)),
    ...DEFAULT_ORDER.filter(k => !rawOrder.includes(k))
  ];

  const moveUp = (index) => {
    if (index <= 0) return;
    const next = [...currentOrder];
    const temp = next[index - 1];
    next[index - 1] = next[index];
    next[index] = temp;
    setOrderLocal(next);
  };

  const moveDown = (index) => {
    if (index >= currentOrder.length - 1) return;
    const next = [...currentOrder];
    const temp = next[index + 1];
    next[index + 1] = next[index];
    next[index] = temp;
    setOrderLocal(next);
  };

  const moveToTop = (index) => {
    if (index <= 0) return;
    const item = currentOrder[index];
    const remaining = currentOrder.filter((_, i) => i !== index);
    setOrderLocal([item, ...remaining]);
  };

  const moveToBottom = (index) => {
    if (index >= currentOrder.length - 1) return;
    const item = currentOrder[index];
    const remaining = currentOrder.filter((_, i) => i !== index);
    setOrderLocal([...remaining, item]);
  };

  const resetOrder = () => {
    setOrderLocal(DEFAULT_ORDER);
    toast.success('Section order reset to default. Click Save Changes to apply.');
  };

  const setAllVisibility = (visible) => {
    const next = { ...curVis };
    [...ALL_SECTIONS, ...GLOBAL_SECTIONS].forEach(s => {
      next[s.key] = visible;
    });
    setVisLocal(next);
    toast.success(visible ? 'All sections set to visible' : 'All sections hidden');
  };

  const saveAll = async () => {
    await saveVis();
    await saveOrder();
    toast.success('Section order and visibility saved successfully');
  };

  return (
    <div data-testid="section-visibility" className="space-y-6">
      {/* Dynamic Section Ordering & Visibility */}
      <Card
        title="Rearrange & Manage Page Sections"
        desc="Shift sections UP or DOWN to customize the layout order of your landing page. You can also toggle each section on or off."
        footer={<SaveButton onClick={saveAll}>Save Section Order & Visibility</SaveButton>}
      >
        {/* Quick Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-2 pb-4 mb-4 border-b border-zinc-800">
          <div className="flex items-center gap-2 text-xs text-zinc-400">
            <LayoutList size={16} className="text-[color:var(--gold)]" />
            <span>Total Sections: <b className="text-white">{currentOrder.length}</b></span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setAllVisibility(true)}
              className="px-2.5 py-1 rounded bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white border border-zinc-800 text-xs font-semibold flex items-center gap-1.5 transition"
            >
              <Eye size={13} className="text-emerald-400" />
              <span>Show All</span>
            </button>
            <button
              type="button"
              onClick={() => setAllVisibility(false)}
              className="px-2.5 py-1 rounded bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white border border-zinc-800 text-xs font-semibold flex items-center gap-1.5 transition"
            >
              <EyeOff size={13} className="text-red-400" />
              <span>Hide All</span>
            </button>
            <button
              type="button"
              onClick={resetOrder}
              className="px-2.5 py-1 rounded bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white border border-zinc-800 text-xs font-semibold flex items-center gap-1.5 transition"
            >
              <RotateCcw size={13} className="text-amber-400" />
              <span>Reset Order</span>
            </button>
          </div>
        </div>

        {/* Ordered Sections List */}
        <div className="space-y-2.5">
          {currentOrder.map((key, index) => {
            const section = ALL_SECTIONS.find(s => s.key === key) || { key, label: key, desc: '' };
            const enabled = curVis[key] !== false;
            const isFirst = index === 0;
            const isLast = index === currentOrder.length - 1;

            return (
              <div
                key={key}
                className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 rounded-xl border transition-all ${
                  enabled
                    ? 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'
                    : 'border-zinc-900 bg-zinc-950/40 opacity-60'
                }`}
                data-testid={`section-order-row-${key}`}
              >
                {/* Left: Position Badge + Section Details */}
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-zinc-800/80 border border-zinc-700 text-white font-mono font-bold text-xs flex items-center justify-center shrink-0 shadow-inner">
                    #{index + 1}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white text-sm font-bold tracking-tight">{section.label}</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${
                        enabled ? 'bg-emerald-950/60 text-emerald-400 border border-emerald-800/50' : 'bg-red-950/60 text-red-400 border border-red-800/50'
                      }`}>
                        {enabled ? 'Active' : 'Hidden'}
                      </span>
                    </div>
                    <p className="text-zinc-500 text-xs mt-0.5 truncate max-w-md">{section.desc}</p>
                  </div>
                </div>

                {/* Right: Shift Up/Down Controls + Visibility Toggle */}
                <div className="flex items-center gap-2 sm:self-center shrink-0">
                  {/* Shift Up */}
                  <button
                    type="button"
                    disabled={isFirst}
                    onClick={() => moveUp(index)}
                    className="p-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 disabled:opacity-30 disabled:pointer-events-none text-zinc-200 hover:text-white border border-zinc-700 transition active:scale-95"
                    title="Move Up"
                    aria-label={`Move ${section.label} up`}
                    data-testid={`btn-move-up-${key}`}
                  >
                    <ArrowUp size={15} />
                  </button>

                  {/* Shift Down */}
                  <button
                    type="button"
                    disabled={isLast}
                    onClick={() => moveDown(index)}
                    className="p-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 disabled:opacity-30 disabled:pointer-events-none text-zinc-200 hover:text-white border border-zinc-700 transition active:scale-95"
                    title="Move Down"
                    aria-label={`Move ${section.label} down`}
                    data-testid={`btn-move-down-${key}`}
                  >
                    <ArrowDown size={15} />
                  </button>

                  {/* Move to Top */}
                  <button
                    type="button"
                    disabled={isFirst}
                    onClick={() => moveToTop(index)}
                    className="hidden sm:inline-flex p-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 disabled:opacity-30 disabled:pointer-events-none text-zinc-400 hover:text-white border border-zinc-700 transition active:scale-95"
                    title="Move to Top"
                    aria-label={`Move ${section.label} to top`}
                  >
                    <ChevronsUp size={15} />
                  </button>

                  {/* Move to Bottom */}
                  <button
                    type="button"
                    disabled={isLast}
                    onClick={() => moveToBottom(index)}
                    className="hidden sm:inline-flex p-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 disabled:opacity-30 disabled:pointer-events-none text-zinc-400 hover:text-white border border-zinc-700 transition active:scale-95"
                    title="Move to Bottom"
                    aria-label={`Move ${section.label} to bottom`}
                  >
                    <ChevronsDown size={15} />
                  </button>

                  <div className="w-px h-6 bg-zinc-800 mx-1" />

                  {/* On/Off Switch */}
                  <div className="flex items-center gap-2 pl-1">
                    <Switch
                      checked={enabled}
                      onCheckedChange={(v) => setVis(key, v)}
                      data-testid={`sv-toggle-${key}`}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Global Extra Elements Visibility */}
      <Card
        title="Global Elements Visibility"
        desc="Show or hide global overlays and side panels."
        footer={<SaveButton onClick={saveVis} />}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {GLOBAL_SECTIONS.map((s) => {
            const enabled = curVis[s.key] !== false;
            return (
              <label
                key={s.key}
                className="flex items-start gap-3 p-3 rounded-lg border border-zinc-800 bg-zinc-900/40 cursor-pointer hover:border-[color:var(--gold)]/40"
                data-testid={`sv-row-${s.key}`}
              >
                <Switch checked={enabled} onCheckedChange={(v) => setVis(s.key, v)} data-testid={`sv-toggle-${s.key}`} />
                <div className="min-w-0">
                  <div className="text-white text-sm font-bold">{s.label}</div>
                  <div className="text-zinc-500 text-xs mt-0.5">{s.desc}</div>
                  <div className={`text-[10px] font-bold mt-1 uppercase tracking-wider ${enabled ? 'text-emerald-400' : 'text-red-400'}`}>
                    {enabled ? 'Visible' : 'Hidden'}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      </Card>

      {/* Hero Slider Controls */}
      <Card
        title="Hero Slider Navigation Controls"
        desc="Show / hide the left-right navigation arrows and dot indicators on the hero slider."
        footer={<SaveButton onClick={saveHeroCtrls} />}
      >
        <div className="flex items-center gap-3">
          <Switch checked={heroCtrls === true} onCheckedChange={setHeroCtrls} data-testid="hero-controls-toggle" />
          <span className="text-sm text-zinc-400">
            {heroCtrls === true
              ? 'Slider arrows & dots are visible'
              : 'Slider arrows & dots are hidden (slides still auto-advance)'}
          </span>
        </div>
      </Card>
    </div>
  );
}
